import { memo } from 'react'
import { useSelector } from 'react-redux'
import { Box } from '@mui/material'
import * as R from 'ramda'
import { useFields } from '@pbt/pbt-ui-components'

import { usePracticeFieldsSection } from '~/components/dashboard/admin/general/practices/hooks/usePracticeFieldsSection'
import FeatureToggle from '~/constants/featureToggle'
import {
  type BusinessSettingsSections,
  getSectionFields,
} from '~/store/duck/businessSettings'
import { getFeatureToggle } from '~/store/reducers/constants'
import type {
  BasePracticeDetailsSectionProps,
  PracticeAppointmentConfigurationFields,
} from '~/types'

import { PracticeDetailsPanels } from '../../practices'
import { AppointmentCancellationReason } from './AppointmentCancellationReason'
import { AppointmentDropOffSettings } from './AppointmentDropOffSettings'
import OccupyAllStaffTimeSlotSwitch from './OccupyAllStaffTimeSlotSwitch'

const AppointmentConfiguration = ({
  business,
}: BasePracticeDetailsSectionProps) => {
  const appointmentConfigurationFields = useSelector(
    getSectionFields<
      BusinessSettingsSections['APPOINTMENT_CONFIGURATION']['fields']
    >(business.id, PracticeDetailsPanels.APPOINTMENT_CONFIGURATION),
  )
  const isAppointmentCancellationReasonEnabled = useSelector(
    getFeatureToggle(FeatureToggle.APPOINTMENT_CANCELLATION_REASON),
  )
  const isDropOffEnabled = useSelector(
    getFeatureToggle(FeatureToggle.APPOINTMENT_DROP_OFF),
  )
  const isBusinessOmniChannel = business.omniChannel
  const shouldShowAppointmentCancellationToggle =
    isAppointmentCancellationReasonEnabled && isBusinessOmniChannel

  const { fields, validate, reset } = useFields(
    [
      {
        name: 'appointmentCancellationReasonEnabled',
        type: 'toggle',
        initialValue: business?.appointmentCancellationReasonEnabled || false,
      },
      {
        name: 'defaultPickUpHandlerId',
        type: 'text',
        initialValue: business?.defaultPickUpHandler?.id ?? '',
      },
    ],
    false,
  )

  const { appointmentCancellationReasonEnabled, defaultPickUpHandlerId } =
    fields

  usePracticeFieldsSection<PracticeAppointmentConfigurationFields>({
    business,
    fields,
    parsedFields: {
      ...appointmentConfigurationFields,
      appointmentCancellationReasonEnabled:
        isAppointmentCancellationReasonEnabled
          ? appointmentCancellationReasonEnabled.value
          : undefined,
      defaultPickUpHandlerId: isDropOffEnabled
        ? defaultPickUpHandlerId.value
        : undefined,
    },
    sectionName: PracticeDetailsPanels.APPOINTMENT_CONFIGURATION,
    validate,
    reset,
    resetDependencies: [],
  })

  return (
    <>
      {shouldShowAppointmentCancellationToggle && (
        <Box pb={1}>
          <AppointmentCancellationReason
            appointmentCancellationReasonEnabled={
              appointmentCancellationReasonEnabled
            }
            businessId={business.id}
          />
        </Box>
      )}
      <OccupyAllStaffTimeSlotSwitch businessId={business.id} />
      {isDropOffEnabled && (
        <AppointmentDropOffSettings
          businessId={business.id}
          defaultPickUpHandlerId={defaultPickUpHandlerId}
          preloadUser={business.defaultPickUpHandler}
        />
      )}
    </>
  )
}

export default memo(AppointmentConfiguration, (prevProps, nextProps) =>
  R.equals(prevProps.business, nextProps.business),
)
